/**
 * Curation (phase 13d). Mirrors `src/http/admin/`.
 *
 * **Every route here answers `404` to anybody who is not an admin**, including
 * anybody not signed in — not `401`, not `403`, because a status that
 * distinguishes "you may not" from "there is nothing there" tells somebody who
 * guessed a URL that they guessed a real one. So a screen must treat `not_found`
 * as "you are not a curator" rather than as a broken link.
 */

import { apiGet, apiPost, apiSend } from "./core.js";
// One definition of what an account may do, shared with the session view, so the
// admin screen and the signed-in landing cannot drift apart about it.
import type { AccountRole } from "./auth.js";

/** One track waiting for a human. */
export interface QueueLine {
  track_id: string;
  title: string;
  artist_credit: string;
  year: number | null;
  /** `verified` · `probable` · `review` · `curated` (ADR 0012). */
  confidence: string;
  /** Out of every party's selection right now. */
  suspended: boolean;
  votes_keep: number;
  votes_off: number;
  /** **Distinct parties** that said off — five rooms or one table. */
  parties_against: number;
  /** Open ADR 0009 reports, which claim the *year* is wrong. */
  open_reports: number;
}

/** `GET /api/admin/queue` */
export interface QueueView {
  tracks: QueueLine[];
  waiting: number;
  /** How many distinct parties suspend a track. The server owns this number. */
  parties_to_suspend: number;
}

/** What one source said, and where to go and look. */
export interface SourceView {
  /** `musicbrainz` · `wikidata` · `curator`. */
  source: string;
  year: number;
  reference: string | null;
}

/** One player's vote. Carries a party, never a person. */
export interface VoteView {
  party_id: string | null;
  verdict: string;
  reason: string | null;
}

/** A decision somebody already made about this track. */
export interface DecisionView {
  action: string;
  changes: unknown;
  note: string | null;
  decided_at: string;
}

/** `GET /api/admin/tracks/{id}` */
export interface TrackView {
  track_id: string;
  title: string;
  artist_credit: string;
  year: number | null;
  confidence: string;
  year_spread: number | null;
  suspended: boolean;
  removed: boolean;
  /** **What each source said.** The reason this screen exists. */
  sources: SourceView[];
  votes: VoteView[];
  decisions: DecisionView[];
}

/** What an admin action answers. */
export interface Decided {
  done: boolean;
  message: string;
}

/** One genre, as the installation's scope editor shows it. */
export interface ScopeGenre {
  id: string;
  slug: string;
  name: string;
  parent_id: string | null;
  /** Whether this installation serves it. */
  chosen: boolean;
}

/**
 * `GET /api/admin/scope` — what this installation serves (phase 16g-2).
 *
 * ⚠️ `genres` is **every** genre, not the scoped list: this is the screen that
 * widens the scope, so it cannot be narrowed by it. `chosen` says which are in.
 */
export interface ScopeView {
  genres: ScopeGenre[];
  decades: number[];
  /** Empty means every decade. */
  chosen_decades: number[];
  playable_tracks: number;
  serves_everything: boolean;
}

/**
 * One of the game's numbers — phase 22, **ADR 0031**.
 *
 * `team_commit` is the only string; everything else is a number, and nothing is
 * a boolean today. The union is written out anyway because the server's own
 * `Rules` is free to grow one and a screen that silently dropped it would be the
 * drift this shape exists to avoid.
 */
export type RuleValue = number | string | boolean;

/**
 * The server's `crate::game::Rules`, **as data rather than as a shape**.
 *
 * 🔴 **Deliberately not a 48-field interface, and this is the load-bearing
 * decision in the panel.** `src/http/admin/rules.rs` serializes `Rules` itself
 * rather than a config-shaped mirror, for the reason `src/config/mod.rs` gives:
 * *"a mapping layer between two structs with the same fields exists only to
 * drift"*. Hand-writing that mirror **here** — where nothing checks it, because
 * the plain-HTTP half has no generator — would reintroduce exactly the drift the
 * Rust side refuses, one language further out.
 *
 * So the editor is driven by the keys the server actually sent. A knob added to
 * `Rules` appears on the screen with no frontend change at all, and one removed
 * stops appearing. What is given up is per-field static typing, which for a
 * config editor buys nothing: every field is a number in a box either way.
 *
 * Two levels, because `coins` and `shop` are nested.
 */
export type ServerRules = Record<string, RuleValue | Record<string, RuleValue>>;

/**
 * The rules as this installation actually plays them — phase 23.
 *
 * ⚠️ **A hand-kept mirror of `crate::game::Rules`, and the one place in this
 * codebase where that is the right call.** The admin editor deliberately avoids
 * it (see [`ServerRules`]) because it only ever puts numbers in boxes. The rules
 * *page* writes sentences about specific numbers, so it has to name them — and a
 * renamed field would otherwise reach a player as the word `undefined` in the
 * middle of an explanation.
 *
 * 📌 Nothing on this side can check it, so the check is on the Rust side:
 * `the_rules_page_gets_every_number_it_renders` in `tests/http_rules.rs` walks
 * the list of keys this interface needs and fails if one stops being sent.
 */
export interface PlayedRules {
  cards_to_win: number;
  clip_seconds: number;
  guess_seconds: number;
  place_seconds: number;
  challenge_window_seconds: number;
  reveal_seconds: number;
  dispute_window_seconds: number;
  dispute_corroborations: number;
  max_voids_per_game: number;
  steal_defence_seconds: number;
  max_steals_per_side: number;
  max_team_revotes: number;
  stall_grace_seconds: number;
  stall_vote_seconds: number;
  max_wildcards_per_ten: number;
  guess_options: number;
  coins: {
    artist: number;
    title: number;
    year_slot: number;
    exact_year_bonus: number;
    undated_pair: number;
  };
  shop: {
    challenge: number;
    steal: number;
    skip: number;
    narrow_options: number;
    blind_timeline: number;
    insurance: number;
    steal_defence: number;
    team_revote: number;
  };
}

/** `GET /api/rules`. */
export interface PlayedRulesView {
  rules: PlayedRules;
  /**
   * Whether these came from the installation's own rule set, or from the
   * configuration that would have seeded one.
   *
   * On the wire so the page never has to guess — an installation whose admin
   * deactivated every set plays by `[game]`, and a page that said otherwise
   * would be confidently wrong about the one thing it exists for.
   */
  from_installation: boolean;
}

/** One named set of rules. */
export interface RuleSetRow {
  id: string;
  name: string;
  /** Whether a party starting now would play by these. */
  is_active: boolean;
  rules: ServerRules;
}

/** `GET /api/admin/rules`. */
export interface RulesView {
  /** Every set, active one first. */
  sets: RuleSetRow[];
  /**
   * What `[game]` in `sonolume.toml` says.
   *
   * Shown so the screen can tell an operator the file is **not** being read —
   * on the screen, rather than only in a startup log they will never see.
   */
  configured: ServerRules;
  /** The built-in defaults, so *reset* needs no second copy on the client. */
  defaults: ServerRules;
}

/** One pipeline run, as the admin's screen shows it — phase 16i. */
export interface RunView {
  id: string;
  kind: string;
  /** `pending` · `running` · `done` · `failed` · `cancelled`. */
  status: string;
  /** What the handler last said. */
  progress: { phase?: string; done?: number; total?: number | null };
  result: {
    examined: number;
    written: number;
    skipped: number;
    cancelled: boolean;
  } | null;
  last_error: string | null;
  attempts: number;
  /**
   * ⚠️ Asked to stop **and not stopped yet**.
   *
   * A running stage notices at its next checkpoint — at most two seconds for a
   * looping one, and not until it finishes for a blocking dump scan. The screen
   * has to say "stopping" for that window rather than claiming it stopped.
   */
  stopping: boolean;
  requested_by: string | null;
  created_at: string;
  progress_at: string | null;
  finished_at: string | null;
}

/** `GET /api/admin/jobs` */
export interface JobsView {
  /**
   * The stages that may be launched, **in the order a fresh install runs
   * them** — each reads what the previous one wrote.
   */
  launchable: string[];
  runs: RunView[];
}

/** One bucket of the status page. */
export interface StatusCount {
  key: string;
  count: number;
}

/** `GET /api/admin/status` */
export interface StatusView {
  tracks_by_confidence: StatusCount[];
  suspended: number;
  removed: number;
  with_audio: number;
  jobs_by_status: StatusCount[];
  queue_waiting: number;
  live_parties: number;
}

/** Whether an account has been let into this installation — phase 19a. */

export type AccountStatus = "pending" | "active" | "rejected";

/**
 * One account, as `/admin` renders it — phase 20f-3, ADR 0028.
 *
 * Mirrors `approval::AccountRow` in `src/store/identity/accounts/`. Like
 * everything else in this file it is **hand-kept**: the plain-HTTP half has no
 * generator, so a change on the Rust side has to be made here too.
 */
export interface AccountRow {
  id: string;
  email: string;
  /** The name on their first profile. `null` should not happen — see the store. */
  display_name: string | null;
  /** Whether they clicked the verification link. The one non-self-asserted fact. */
  email_verified: boolean;
  status: AccountStatus;
  /** Re-exported from `http/auth.ts`: one definition, so the two cannot drift. */
  role: AccountRole;
  /**
   * Whether removing this account's role, or turning it away, would leave the
   * installation with **no administrator** — and therefore reopen `/setup`.
   *
   * ⚠️ **A hint for the screen, never the check.** `accounts::set_role` enforces
   * it, and it has to: `sonolume-ingest` never renders this.
   */
  last_admin: boolean;
  created_at: string;
}

/**
 * `GET /api/admin/accounts` — two disjoint lists.
 *
 * Split rather than one list sorted "waiting first", because a wave of
 * registrations would otherwise fill the cap and make every existing account
 * invisible — including the one you came to promote.
 */
export interface ApprovalQueue {
  /** Everybody waiting, oldest first: a queue that is not FIFO makes somebody wait forever. */
  waiting: AccountRow[];
  /** Everybody already decided about, newest first. */
  settled: AccountRow[];
  /** How many are waiting in total, which may exceed `waiting.length`. */
  pending: number;
  /** How many settled accounts exist, which may exceed `settled.length`. */
  total: number;
}

/** What a decision answers with. */
export interface AccountDecided {
  account_id: string;
  status: AccountStatus;
  /** What it may now do — phase 20f-3. */
  /** Re-exported from `http/auth.ts`: one definition, so the two cannot drift. */
  role: AccountRole;
  /** Still waiting, so the caller need not re-fetch the queue. */
  pending: number;
}

/** Who is waiting to be let in. */
export async function fetchApprovalQueue(): Promise<ApprovalQueue> {
  return apiGet<ApprovalQueue>("/admin/accounts");
}

/** Let somebody in. Idempotent, so a double-click is not an error. */
export async function approveAccount(accountId: string): Promise<AccountDecided> {
  return apiPost<AccountDecided>(
    `/admin/accounts/${encodeURIComponent(accountId)}/approve`,
    {},
  );
}

/**
 * Turn somebody away.
 *
 * The account is **not deleted** — see migration 0023. Deleting would free the
 * address to register again and rejoin the queue, turning a decision into a
 * delay.
 */
export async function rejectAccount(accountId: string): Promise<AccountDecided> {
  return apiPost<AccountDecided>(
    `/admin/accounts/${encodeURIComponent(accountId)}/reject`,
    {},
  );
}

/**
 * Make somebody else an administrator — ADR 0028.
 *
 * Approves them on the way if they were still waiting: an inactive admin is
 * unrepresentable (`accounts_admin_is_active`), and granting the role already
 * means letting them in.
 */
export async function promoteAccount(accountId: string): Promise<AccountDecided> {
  return apiPost<AccountDecided>(
    `/admin/accounts/${encodeURIComponent(accountId)}/promote`,
    {},
  );
}

/**
 * Take the role back.
 *
 * Rejects with a 409 for the **last** administrator: `/setup` is offered exactly
 * while no admin exists, so an installation that loses its last one puts its own
 * first-run route back on a live deployment.
 */
export async function demoteAccount(accountId: string): Promise<AccountDecided> {
  return apiPost<AccountDecided>(
    `/admin/accounts/${encodeURIComponent(accountId)}/demote`,
    {},
  );
}

/** What needs a human. */
export async function fetchQueue(): Promise<QueueView> {
  return apiGet<QueueView>("/admin/queue");
}

/** One track, and everything said about it. */
export async function fetchTrack(trackId: string): Promise<TrackView> {
  return apiGet<TrackView>(`/admin/tracks/${encodeURIComponent(trackId)}`);
}

/** Answer the votes and put the track back in play. */
export async function keepTrack(trackId: string, note: string | null): Promise<Decided> {
  return apiPost<Decided>(`/admin/tracks/${encodeURIComponent(trackId)}/keep`, { note });
}

/** Take the track out of the catalog — softly, so the decision survives. */
export async function removeTrack(
  trackId: string,
  note: string | null,
): Promise<Decided> {
  return apiPost<Decided>(`/admin/tracks/${encodeURIComponent(trackId)}/remove`, {
    note,
  });
}

/**
 * Correct a track's metadata.
 *
 * **A year makes it `curated`** (ADR 0012) and is recorded beside whatever the
 * machines said; a title or artist fix is not a claim about the year and does
 * not. Every field is optional and at least one has to be present.
 */
export async function correctTrack(
  trackId: string,
  correction: {
    year?: number;
    title?: string;
    artist_credit?: string;
    note?: string;
  },
): Promise<Decided> {
  return apiPost<Decided>(
    `/admin/tracks/${encodeURIComponent(trackId)}/correct`,
    correction,
  );
}

/** What this installation serves. */
export async function fetchScope(): Promise<ScopeView> {
  return apiGet<ScopeView>("/admin/scope");
}

/**
 * Replace it. **Empty lists mean "serve everything"**, which is also how an
 * installation undoes a narrowing.
 */
export async function saveScope(
  genres: string[],
  decades: number[],
): Promise<{ ok: boolean; playable_tracks: number }> {
  return apiSend("PUT", "/admin/scope", { genres, decades });
}

/**
 * The rules this installation plays by — phase 23, for the `/rules` page.
 *
 * Signed in, like everything else (ADR 0027), and **not** the admin route: this
 * is what a player reads, and it carries no presets and no config.
 */
export async function fetchPlayedRules(): Promise<PlayedRulesView> {
  return apiGet<PlayedRulesView>("/rules");
}

/** Every rule set, plus what the file and the defaults say. */
export async function fetchRules(): Promise<RulesView> {
  return apiGet<RulesView>("/admin/rules");
}

/** Add a preset. **Never active on creation** — activating is its own act. */
export async function createRuleSet(
  name: string,
  rules: ServerRules,
): Promise<RuleSetRow> {
  return apiPost("/admin/rules", { name, rules });
}

/** Replace one set's rules, and optionally rename it. */
export async function saveRuleSet(
  id: string,
  name: string,
  rules: ServerRules,
): Promise<unknown> {
  return apiSend("PUT", `/admin/rules/${id}`, { name, rules });
}

/**
 * Make this the set every party plays by.
 *
 * ⚠️ **From the next game**, not immediately: a party in flight keeps the rules
 * it started with, so nothing can rewrite what a card is worth halfway through.
 */
export async function activateRuleSet(id: string): Promise<unknown> {
  return apiPost(`/admin/rules/${id}/activate`, {});
}

/** Remove a preset. The active one cannot be deleted. */
export async function deleteRuleSet(id: string): Promise<unknown> {
  return apiSend("DELETE", `/admin/rules/${id}`, {});
}

/** The pipeline: what can be launched, and what has run. */
export async function fetchJobs(): Promise<JobsView> {
  return apiGet<JobsView>("/admin/jobs");
}

/**
 * Queue a stage.
 *
 * `queued: false` means an identical one is already live, which is **not an
 * error** — two `load_mb_dates` runs would be two workers reading the same
 * 57 GB export and writing the same tracks.
 */
export async function launchJob(
  kind: string,
  limit?: number,
): Promise<{ queued: boolean; id: string | null }> {
  return apiPost("/admin/jobs", limit === undefined ? { kind } : { kind, limit });
}

/** Ask a job to stop. Cooperative — see `RunView.stopping`. */
export async function cancelJob(id: string): Promise<{ ok: boolean }> {
  return apiPost(`/admin/jobs/${encodeURIComponent(id)}/cancel`, {});
}

/** Is anything stuck? */
export async function fetchAdminStatus(): Promise<StatusView> {
  return apiGet<StatusView>("/admin/status");
}
